import { Tooltip } from '@ads/components-react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';

/** Props de `PlanReadOnlyField`. */
export interface PlanReadOnlyFieldProps {
  /** Rótulo do campo. */
  readonly label: string;
  /** Valor já formatado; ignorado quando `children` é passado. */
  readonly value?: string | undefined;
  /** Conteúdo customizado — usado quando o valor é uma Tag em vez de texto. */
  readonly children?: ReactNode | undefined;
  /** Texto secundário abaixo do valor. */
  readonly hint?: string | null | undefined;
  /**
   * Texto do tooltip do ícone de informação ao lado do rótulo.
   *
   * É o texto, não um booleano: antes a prop só ligava o ícone, que ficava
   * decorativo. Um ícone que promete explicação e não entrega é pior que a
   * ausência dele, então o ícone só existe quando há o que dizer.
   */
  readonly info?: string | undefined;
  /** Destaca o valor — usado no número que resume o bloco. */
  readonly emphasis?: boolean | undefined;
}

/**
 * Campo somente leitura do formulário de edição de plano.
 *
 * Deliberadamente **não** é um input desabilitado, como faz a tela legada: um
 * input cinza convida o operador a tentar editar um valor que o backend calcula.
 */
export function PlanReadOnlyField({
  label,
  value,
  children,
  hint,
  info,
  emphasis,
}: PlanReadOnlyFieldProps) {
  return (
    <div className="flex flex-1 flex-col gap-quark">
      <span className="flex items-center gap-quark">
        <span className="ts-paragraph-xxs text-ctx-content-placeholder">{label}</span>
        {info ? (
          <Tooltip title={info}>
            <InformationCircleIcon className="size-mini shrink-0 text-ctx-content-placeholder" />
          </Tooltip>
        ) : null}
      </span>
      {children ?? (
        <span
          className={
            emphasis
              ? 'break-words ts-heading-xs text-ctx-content-title'
              : 'break-words ts-paragraph-xs text-ctx-content-title'
          }
        >
          {value}
        </span>
      )}
      {hint ? <span className="ts-paragraph-xxs text-ctx-content-placeholder">{hint}</span> : null}
    </div>
  );
}
